import type {
  Annotation,
  LoopIterationContext,
  Task,
  TaskGroup,
  Visitor,
  WorkflowVersionEvent,
} from '../../api/types';
import type { EditorApi } from './editor-api';

export interface WorkflowEventContext {
  editorApi: EditorApi;
  visitors: Visitor[];
  setVisitors: (visitors: Visitor[]) => void;
  onTaskConfigChanged?: (
    taskId: number,
    event: { pendingInteractionChanged?: boolean }
  ) => void;
  onPendingInteractionChanged?: () => void;
  onTaskUpdated?: (task: Task) => void;
  onTaskStateChanged?: (
    taskId: number,
    state: {
      status?: string;
      disabled?: boolean;
      interactionPending?: boolean;
      loopIteration?: LoopIterationContext | null;
      pendingInteractionChanged?: boolean;
    }
  ) => void;
  onTaskOutputEmitted?: (taskId: number) => void;
  onTaskGroupUpsert?: (group: TaskGroup) => void;
  onTaskGroupRemoved?: (groupId: number) => void;
  onAnnotationsUpdated?: (annotations: Annotation[]) => void;
  onWorkflowVersionChanged?: (event: WorkflowVersionEvent) => void;
}

// Event payloads expected from the backend/websocket
type TaskStateChanged = {
  type: 'taskStateChanged';
  taskId: number;
  status?: string;
  disabled?: boolean;
  interactionPending?: boolean;
  loopIteration?: LoopIterationContext | null;
  visual?: unknown;
  pendingInteractionChanged?: boolean;
};
type TaskAdded = { type: 'taskAdded'; task: unknown };
type TaskRemoved = {
  type: 'taskRemoved';
  taskId: number;
  pendingInteractionChanged?: boolean;
};
type TaskUpdated = { type: 'taskUpdated'; task: Task };
type TaskConfigChanged = {
  type: 'taskConfigChanged';
  taskId: number;
  pendingInteractionChanged?: boolean;
};
type TaskOutputEmitted = {
  type: 'taskOutputEmitted';
  taskId: number;
  loopIteration?: LoopIterationContext | null;
};
type ConnectionAdded = {
  type: 'connectionAdded';
  connection: {
    id: number;
    fromId: number;
    toId: number;
    fromField: string;
    toField: string;
    name: string;
  };
};
type ConnectionUpdated = {
  type: 'connectionUpdated';
  connection: {
    id: number;
    name: string;
  };
};
type ConnectionRemoved = { type: 'connectionRemoved'; connectionId: number };
type VisitorCreated = { type: 'visitorCreated'; visitor: Visitor };
type VisitorDeleted = { type: 'visitorDeleted'; visitorId: string };
type VisitorCursorUpdated = {
  type: 'visitorCursorUpdated';
  visitorId: string;
  cursor: { x: number; y: number };
};
type TaskGroupAdded = { type: 'taskGroupAdded'; group: TaskGroup };
type TaskGroupUpdated = { type: 'taskGroupUpdated'; group: TaskGroup };
type TaskGroupRemoved = { type: 'taskGroupRemoved'; groupId: number };
type RuntimeValueEvent =
  | { type: 'runtimeValueCreated'; [k: string]: unknown }
  | { type: 'runtimeValueChanged'; [k: string]: unknown }
  | { type: 'runtimeValueRemoved'; [k: string]: unknown };

type WorkflowUpdated = {
  type: 'workflowUpdated';
  workflowId: number;
  cronLastExecutedAt?: string | null;
  cronNextExecutionAt?: string | null;
};

type AnnotationsUpdated = {
  type: 'annotationsUpdated';
  workflowId: number;
  annotations: Annotation[] | null;
};

type WorkflowVersionChanged = {
  type: 'workflowVersionChanged';
  workflowId: number;
  event: WorkflowVersionEvent;
};

type WorkflowEvent =
  | TaskStateChanged
  | TaskAdded
  | TaskRemoved
  | TaskUpdated
  | TaskConfigChanged
  | TaskOutputEmitted
  | ConnectionAdded
  | ConnectionUpdated
  | ConnectionRemoved
  | VisitorCreated
  | VisitorDeleted
  | VisitorCursorUpdated
  | TaskGroupAdded
  | TaskGroupUpdated
  | TaskGroupRemoved
  | RuntimeValueEvent
  | WorkflowUpdated
  | AnnotationsUpdated
  | WorkflowVersionChanged;

export function handleWorkflowEvent(
  ctx: WorkflowEventContext,
  data: WorkflowEvent
): void {
  switch (data.type) {
    case 'taskStateChanged': {
      const {
        taskId,
        type: _t,
        pendingInteractionChanged,
        visual: _visual,
        ...editorState
      } = data;
      ctx.editorApi.updateTaskState(taskId, editorState);
      const state =
        pendingInteractionChanged === undefined
          ? editorState
          : { ...editorState, pendingInteractionChanged };
      ctx.onTaskStateChanged?.(taskId, state);
      if (pendingInteractionChanged) {
        ctx.onPendingInteractionChanged?.();
      }
      break;
    }
    case 'taskOutputEmitted':
      ctx.onTaskOutputEmitted?.(data.taskId);
      break;
    case 'taskAdded':
      ctx.editorApi.addTask(data.task);
      break;
    case 'taskRemoved':
      ctx.editorApi.removeTask(data.taskId);
      if (data.pendingInteractionChanged) {
        ctx.onPendingInteractionChanged?.();
      }
      break;
    case 'taskUpdated':
      ctx.editorApi.updateTask(data.task);
      ctx.onTaskUpdated?.(data.task);
      break;
    case 'taskConfigChanged':
      ctx.onTaskConfigChanged?.(data.taskId, {
        pendingInteractionChanged: data.pendingInteractionChanged,
      });
      if (data.pendingInteractionChanged) {
        ctx.onPendingInteractionChanged?.();
      }
      break;
    case 'connectionAdded':
      ctx.editorApi.addConnection(data.connection);
      break;
    case 'connectionUpdated':
      ctx.editorApi.updateConnectionName(
        data.connection.id,
        data.connection.name
      );
      break;
    case 'connectionRemoved':
      ctx.editorApi.removeConnection(data.connectionId);
      break;
    case 'visitorCreated': {
      const v = data.visitor;
      ctx.setVisitors([...ctx.visitors.filter((x) => x.id !== v.id), v]);
      break;
    }
    case 'visitorDeleted':
      ctx.setVisitors(ctx.visitors.filter((v) => v.id !== data.visitorId));
      ctx.editorApi.removeVisitorCursor(data.visitorId);
      break;
    case 'visitorCursorUpdated': {
      const visitor = ctx.visitors.find((v) => v.id === data.visitorId);
      if (visitor)
        ctx.editorApi.updateVisitorCursor(
          data.visitorId,
          visitor.color,
          data.cursor
        );
      break;
    }
    case 'taskGroupAdded':
    case 'taskGroupUpdated':
      ctx.onTaskGroupUpsert?.(data.group);
      break;
    case 'taskGroupRemoved':
      ctx.onTaskGroupRemoved?.(data.groupId);
      break;
    case 'runtimeValueCreated':
    case 'runtimeValueChanged':
    case 'runtimeValueRemoved':
      window.dispatchEvent(new CustomEvent(data.type, { detail: data }));
      break;
    case 'workflowUpdated':
      // Dispatch custom event for workflow updates (e.g., cron execution times)
      window.dispatchEvent(
        new CustomEvent('workflowUpdated', { detail: data })
      );
      break;
    case 'annotationsUpdated': {
      const next = Array.isArray(data.annotations) ? data.annotations : [];
      if (ctx.onAnnotationsUpdated) {
        ctx.onAnnotationsUpdated(next);
      } else {
        ctx.editorApi.setAnnotations?.(next);
      }
      break;
    }
    case 'workflowVersionChanged':
      ctx.onWorkflowVersionChanged?.(data.event);
      break;
  }
}
